// ---------------------------------------------------------------------------
// DrillDownBadge -- Drill-Down Context Indicator
// ---------------------------------------------------------------------------
// Displays a prominent badge showing the active drill-down filter context.
// Allows user to clear the drill-down and return to unfiltered view.
// ---------------------------------------------------------------------------

import React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DrillDownBadgeProps {
  /** Label prefix (e.g., "Profile", "Brand") */
  readonly sourceLabel: string;
  /** Display value (e.g., "slbenfica", "Nike") */
  readonly displayValue: string;
  /** Callback when user clicks to clear drill-down */
  readonly onClear: () => void;
  /** Optional className for customization */
  readonly className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DrillDownBadge({
  sourceLabel,
  displayValue,
  onClear,
  className,
}: DrillDownBadgeProps) {
  const words = displayValue.split(/\s+/);
  const truncatedValue = words.length > 2 ? `${words.slice(0, 2).join(' ')}…` : displayValue;

  return (
    <div
      data-testid="drill-down-badge"
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1 rounded-full',
        'bg-v11-accent/10 border border-v11-accent/30',
        'text-xs max-w-md',
        className
      )}
      title={words.length > 2 ? displayValue : undefined}
    >
      {/* Label and value */}
      <span className="text-v11-text-muted shrink-0">{sourceLabel}:</span>
      <span className="font-medium text-v11-text-primary truncate">{truncatedValue}</span>

      {/* Clear button */}
      <button
        type="button"
        onClick={onClear}
        data-testid="drill-down-clear"
        className="text-v11-text-muted hover:text-v11-text-primary transition-colors"
        aria-label={`Clear ${sourceLabel} filter and return to all results`}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
